import React, { useMemo } from 'react';
import { Flex, HStack, Icon, VStack, Text, DataList, DataItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventQueueView } from '@mark43/cad-api';
import { FrPriorityChip } from './fr-priority-chip';
import { FormattedDurationSince } from '../common/components/formatted-duration-since';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { CautionPills } from '../event-panel/tabs/cautions/components/caution-pills';
import { EsriMapEntityTypeEnum } from '../esri/types';
import { useViewLocationOnMap } from '../location/hooks/use-view-location-on-map';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';
import { isValidLatLng } from '../location/helpers';
import { EventLabelsView } from '../event-labels-view';
import { FrQueueRowWrapper } from './fr-queue-row-wrapper';
import { useBootstrapState, UseBootstrapStateReturns } from '../bootstrap/bootstrap-provider';
import { getPrimarySubdivisionNameKeyByAgencyType } from '../map-window/components/helpers';
import { AgencyTypeGlobal } from '../common/global-attributes';
import { FrAssignButton } from '../fr-event-common-components/fr-assign-button';
import { getEventDataForSelfAssignmentModal } from '../fr-event-common-components/helpers';
import { Tooltip } from '../common/components/tooltip';
import { FlagRenderer } from '../queues/components/flag-renderer';
import { FrEventUnitPills } from '../fr-event-common-components/fr-event-unit-pills';
import { DataPrivacy } from '../common/components/datadog-privacy';

export const QueueRow = observer(function QueueRow({
    data,
    assignedEvent,
    isFirstRow,
}: {
    data: EventQueueView;
    assignedEvent?: EventQueueView;
    isFirstRow?: boolean;
}): React.ReactElement {
    const bootstrapState = useBootstrapState();
    const primarySubdivisionNameKey = getPrimarySubdivisionNameKeyByAgencyType(
        data?.agencyTypeGlobalAttrId as AgencyTypeGlobal
    );
    const primarySubdivisionName =
        bootstrapState[primarySubdivisionNameKey as keyof UseBootstrapStateReturns];

    const unitAssignment = useUnitAssignment();
    const { assignSelfToEvents } = useEnabledFeatures();
    const viewLocationOnMap = useViewLocationOnMap();
    const openFrEventDetails = useOpenFrEventDetails();

    const hasAssignedUnits = !!data.assignedUnits?.length;

    const selectedEvent = getEventDataForSelfAssignmentModal({ ...data, eventId: data.id });

    const modifiedAssignedEvent = getEventDataForSelfAssignmentModal({
        ...assignedEvent,
        eventId: assignedEvent?.id,
    });

    const isUnitAssignedToCurrentEvent = useMemo(
        () => (data.assignedUnits || []).some((unit) => unit.id === unitAssignment?.unitId),
        [data.assignedUnits, unitAssignment?.unitId]
    );
    const showAssignButton =
        !!unitAssignment && assignSelfToEvents && !isUnitAssignedToCurrentEvent;

    const eventLatitude = data.latitude;
    const eventLongitude = data.longitude;
    const isValidLocation = isValidLatLng(eventLatitude, eventLongitude);
    const hasLocation = !!(data.distanceFromEvent != null && isValidLocation);

    const handleViewOnMap = () => {
        if (!isValidLocation || !data.id) return;

        viewLocationOnMap({
            entityId: data.id,
            entityType: EsriMapEntityTypeEnum.EVENT,
            latitude: eventLatitude,
            longitude: eventLongitude,
            shouldMoveMapCenterToLoggedInUnit: false,
        });
    };

    const handleOpenEventNarrativeTab = () => {
        if (!data.id) return;

        openFrEventDetails(data.id, FR_EVENT_DETAILS_TABS.NARRATIVE);
    };

    const eventLabelsOptions = useMemo(
        () =>
            data.eventLabels.map((eventLabel) => ({
                label: eventLabel.name,
                locationCautionPriorityGlobalAttrId: eventLabel.cautionLevelAttrId,
                value: eventLabel.id,
            })),
        [data.eventLabels]
    );

    const priority1Cautions = useMemo(
        () => data?.cautionsTabPills?.filter((caution) => caution?.priorityLevel === 1) ?? [],
        [data.cautionsTabPills]
    );

    return (
        <FrQueueRowWrapper
            isFirstRow={isFirstRow}
            leftChildren={
                <HStack justifyContent="space-between" w="100%">
                    <Flex flexDirection="row" columnGap="8px" alignItems="stretch" height="full">
                        <DataPrivacy privacy="unmask"><FrPriorityChip data={data} /></DataPrivacy>
                        <Flex alignItems="center">
                            <VStack alignItems="left">
                                <Text variant="headingMd">{data.eventLocation}</Text>
                                <DataPrivacy privacy="unmask"><Text variant="bodyMd">{data.cfsNumberAndText}</Text></DataPrivacy>
                                {priority1Cautions.length > 0 && (
                                    <CautionPills cautions={priority1Cautions} />
                                )}
                            </VStack>
                        </Flex>
                    </Flex>
                    {isValidLocation && (
                        <Flex alignItems="center">
                            <Icon
                                icon="Crosshairs"
                                color="brand.default"
                                style={{ marginRight: 5 }}
                            />
                        </Flex>
                    )}
                </HStack>
            }
            onClickRightChildrenAction={handleOpenEventNarrativeTab}
            onClickLeftChildrenAction={isValidLocation ? handleViewOnMap : undefined}
            rightChildren={
                <>
                    <VStack alignItems="left">
                        <HStack>
                            {!!data.eventLabels?.length && (
                                <EventLabelsView labels={eventLabelsOptions} truncateCount={2} />
                            )}
                            <Tooltip overlay={<FlagRenderer breakLines value={data.narrative} />}>
                                <Text noOfLines={1} variant="bodyMd">
                                    <FlagRenderer breakLines value={data.narrative} />
                                </Text>
                            </Tooltip>
                        </HStack>
                        <DataList direction="row" wrap="wrap">
                            <DataPrivacy privacy="unmask"><DataItem dataKey="Event">{data.eventNumber}</DataItem></DataPrivacy>
                            <DataPrivacy privacy="unmask"><DataItem dataKey={primarySubdivisionName}>{data.beat}</DataItem></DataPrivacy>
                        </DataList>
                        <DataList direction="row" wrap="wrap">
                            <DataPrivacy privacy="unmask">
                                <DataItem dataKey="Distance">
                                    {hasLocation ? `${data.distanceFromEvent} mi` : undefined}
                                </DataItem>
                            </DataPrivacy>
                            {data?.eventStatus === 'UNASSIGNED' && (
                                <DataPrivacy privacy="unmask">
                                    <DataItem dataKey="SM">
                                        {data.timeTransmitted && (
                                            <FormattedDurationSince
                                                date={new Date(data.timeTransmitted)}
                                            />
                                        )}
                                    </DataItem>
                                </DataPrivacy>
                            )}
                            {data?.eventStatus === 'ASSIGNED' && (
                                <DataPrivacy privacy="unmask">
                                    <DataItem dataKey="WM">
                                        {data.timeDispatched && (
                                            <FormattedDurationSince
                                                date={new Date(data.timeDispatched)}
                                            />
                                        )}
                                    </DataItem>
                                </DataPrivacy>
                            )}
                        </DataList>
                        <Flex columnGap="4px">
                            <DataPrivacy privacy="unmask"><FrEventUnitPills units={data.assignedUnits} /></DataPrivacy>
                        </Flex>
                    </VStack>
                    {showAssignButton && (
                        <DataPrivacy privacy="unmask">
                            <FrAssignButton
                                size="xl"
                                hasAssignedUnits={hasAssignedUnits}
                                selectedEvent={selectedEvent}
                                assignedEvent={modifiedAssignedEvent}
                                style={{ minWidth: 140 }}
                            />
                        </DataPrivacy>
                    )}
                </>
            }
        />
    );
});
